import type { ReactNode } from 'react';

/**
 * **Views picked by picture, not by name** (the mockup's inspector note) —
 * a small schematic of each view, drawn in the console's own greys.
 *
 * Deliberately a *schematic*: boxes for pictures, bars for words, one accent
 * for the thing that acts. The real rendering is on the canvas a few inches
 * to the left; what a thumbnail has to do is let the difference between
 * "cards" and "list" be seen before it is clicked. The drawings are per view
 * name, because a name means the same arrangement on every block that has it
 * (the services' `cards` and the testimonials' `cards` are both a row of
 * cards), with a per-block drawing for the blocks that have one look.
 */

/** x, y, width, height on a 100 × 50 board, and what the box stands for. */
type Box = [number, number, number, number, Ink?];
/** `b` a block or picture, `t` a line of words, `a` the accent (a button, a price). */
type Ink = 'b' | 't' | 'a' | 'd';

const cards3: Box[] = [
  [6, 12, 27, 30],
  [37, 12, 27, 30],
  [68, 12, 27, 30],
];
const lines = (n: number, x = 6, w = 88): Box[] =>
  Array.from({ length: n }, (_, i): Box => [x, 10 + i * (32 / n), w, 4, 't']);

const VIEWS: Record<string, Box[]> = {
  /* hero */
  cover: [
    [0, 0, 100, 50, 'd'],
    [8, 26, 46, 6, 't'],
    [8, 35, 30, 3, 't'],
    [8, 41, 18, 5, 'a'],
  ],
  split: [
    [6, 14, 40, 6, 't'],
    [6, 24, 30, 3, 't'],
    [6, 32, 16, 5, 'a'],
    [56, 6, 38, 38],
  ],
  minimal: [
    [22, 16, 56, 6, 't'],
    [30, 26, 40, 3, 't'],
    [41, 33, 18, 5, 'a'],
  ],
  editorial: [
    [6, 8, 60, 10, 't'],
    [6, 22, 44, 3, 't'],
    [60, 22, 34, 22],
  ],
  search: [
    [0, 0, 100, 50, 'd'],
    [14, 14, 72, 6, 't'],
    [14, 28, 58, 8],
    [72, 28, 14, 8, 'a'],
  ],
  /* lists and grids */
  list: [
    ...lines(4, 6, 70),
    [80, 10, 14, 4, 'a'],
    [80, 18, 14, 4, 'a'],
    [80, 26, 14, 4, 'a'],
    [80, 34, 14, 4, 'a'],
  ],
  grid: [
    [6, 8, 42, 16],
    [52, 8, 42, 16],
    [6, 28, 42, 16],
    [52, 28, 42, 16],
  ],
  cards: cards3,
  thumbnails: [
    [6, 8, 12, 12],
    [22, 11, 40, 4, 't'],
    [6, 28, 12, 12],
    [22, 31, 40, 4, 't'],
    [78, 11, 16, 4, 'a'],
    [78, 31, 16, 4, 'a'],
  ],
  tabs: [
    [6, 6, 18, 6, 'a'],
    [26, 6, 18, 6],
    [46, 6, 18, 6],
    ...lines(3, 6, 88).map((b): Box => [b[0], b[1] + 8, b[2], b[3], 't']),
  ],
  groups: [
    [6, 6, 30, 4, 't'],
    [6, 13, 88, 3, 't'],
    [6, 19, 88, 3, 't'],
    [6, 28, 30, 4, 't'],
    [6, 35, 88, 3, 't'],
    [6, 41, 88, 3, 't'],
  ],
  'price-table': [
    [6, 8, 88, 6],
    [6, 18, 60, 3, 't'],
    [80, 18, 14, 3, 'a'],
    [6, 26, 60, 3, 't'],
    [80, 26, 14, 3, 'a'],
    [6, 34, 60, 3, 't'],
    [80, 34, 14, 3, 'a'],
  ],
  /* words */
  stack: [
    [20, 8, 60, 6, 't'],
    [14, 20, 72, 3, 't'],
    [14, 26, 72, 3, 't'],
    [14, 32, 50, 3, 't'],
  ],
  'split-reverse': [
    [6, 6, 38, 38],
    [54, 14, 40, 6, 't'],
    [54, 24, 34, 3, 't'],
    [54, 30, 34, 3, 't'],
  ],
  zigzag: [
    [6, 6, 36, 16],
    [48, 10, 44, 4, 't'],
    [58, 28, 36, 16],
    [6, 32, 44, 4, 't'],
  ],
  quote: [
    [14, 14, 72, 8, 't'],
    [14, 26, 60, 4, 't'],
    [36, 36, 28, 3, 'a'],
  ],
  /* features */
  icons: [
    [10, 12, 8, 8, 'a'],
    [6, 24, 18, 3, 't'],
    [42, 12, 8, 8, 'a'],
    [38, 24, 18, 3, 't'],
    [76, 12, 8, 8, 'a'],
    [72, 24, 18, 3, 't'],
  ],
  pillars: [
    [6, 8, 26, 34],
    [37, 8, 26, 34],
    [68, 8, 26, 34],
    [10, 12, 18, 4, 'a'],
    [41, 12, 18, 4, 'a'],
    [72, 12, 18, 4, 'a'],
  ],
  quiet: lines(4, 14, 72),
  numbered: [
    [6, 10, 6, 6, 'a'],
    [16, 11, 70, 4, 't'],
    [6, 22, 6, 6, 'a'],
    [16, 23, 70, 4, 't'],
    [6, 34, 6, 6, 'a'],
    [16, 35, 70, 4, 't'],
  ],
  stats: [
    [6, 14, 24, 10, 'a'],
    [6, 28, 24, 3, 't'],
    [38, 14, 24, 10, 'a'],
    [38, 28, 24, 3, 't'],
    [70, 14, 24, 10, 'a'],
    [70, 28, 24, 3, 't'],
  ],
  timeline: [
    [20, 6, 2, 38],
    [16, 10, 10, 4, 'a'],
    [30, 10, 50, 4, 't'],
    [16, 24, 10, 4, 'a'],
    [30, 24, 50, 4, 't'],
    [16, 38, 10, 4, 'a'],
    [30, 38, 50, 4, 't'],
  ],
  /* pictures */
  strip: [
    [0, 10, 24, 30],
    [26, 10, 24, 30],
    [52, 10, 24, 30],
    [78, 10, 22, 30],
  ],
  mosaic: [
    [6, 6, 44, 38],
    [54, 6, 40, 17],
    [54, 27, 18, 17],
    [76, 27, 18, 17],
  ],
  feature: [
    [6, 6, 58, 38],
    [68, 6, 26, 17],
    [68, 27, 26, 17],
  ],
  masonry: [
    [6, 6, 26, 22],
    [6, 32, 26, 12],
    [37, 6, 26, 14],
    [37, 24, 26, 20],
    [68, 6, 26, 26],
    [68, 36, 26, 8],
  ],
  filmstrip: [
    [-10, 12, 30, 26],
    [24, 12, 30, 26],
    [58, 12, 30, 26],
    [92, 12, 30, 26],
  ],
  /* questions */
  pills: [
    [6, 10, 24, 7, 'a'],
    [34, 10, 30, 7],
    [68, 10, 24, 7],
    [6, 24, 88, 3, 't'],
    [6, 30, 70, 3, 't'],
  ],
  accordion: [
    [6, 8, 88, 7],
    [6, 18, 88, 7],
    [6, 28, 88, 7],
    [86, 10, 5, 3, 'a'],
    [86, 20, 5, 3, 'a'],
    [86, 30, 5, 3, 'a'],
  ],
  'two-column': [
    [6, 8, 40, 4, 't'],
    [6, 16, 40, 3, 't'],
    [6, 26, 40, 4, 't'],
    [6, 34, 40, 3, 't'],
    [54, 8, 40, 4, 't'],
    [54, 16, 40, 3, 't'],
    [54, 26, 40, 4, 't'],
    [54, 34, 40, 3, 't'],
  ],
  /* forms and cards */
  card: [
    [22, 6, 56, 38],
    [30, 12, 40, 4, 't'],
    [30, 22, 40, 6],
    [38, 32, 24, 6, 'a'],
  ],
  centered: [
    [26, 10, 48, 4, 't'],
    [30, 20, 40, 6],
    [40, 30, 20, 6, 'a'],
  ],
  classic: [
    [6, 8, 50, 4, 't'],
    [6, 16, 50, 3, 't'],
    [6, 22, 50, 3, 't'],
    [62, 8, 32, 34],
  ],
  inline: [
    [6, 18, 20, 4, 't'],
    [30, 16, 42, 8],
    [76, 16, 18, 8, 'a'],
  ],
  rail: [
    [6, 8, 56, 34],
    [66, 8, 28, 34],
    [70, 34, 20, 5, 'a'],
  ],
  single: [
    [14, 16, 72, 7, 't'],
    [30, 28, 40, 3, 't'],
  ],
  logos: [
    [6, 20, 16, 8],
    [28, 20, 16, 8],
    [50, 20, 16, 8],
    [72, 20, 16, 8],
  ],
  band: [
    [0, 8, 100, 34, 'd'],
    [10, 18, 50, 5, 't'],
    [70, 18, 20, 7, 'a'],
  ],
  avatars: [
    [10, 12, 14, 14],
    [30, 12, 14, 14],
    [50, 12, 14, 14],
    [70, 12, 14, 14],
    [8, 30, 84, 3, 't'],
  ],
  one: [
    [10, 8, 30, 34],
    [48, 14, 40, 5, 't'],
    [48, 24, 40, 3, 't'],
    [48, 30, 30, 3, 't'],
  ],
  profile: [
    [38, 4, 24, 24],
    [26, 32, 48, 4, 't'],
    [30, 40, 40, 3, 't'],
  ],
  closure: [
    [0, 18, 100, 14, 'd'],
    [20, 23, 60, 4, 't'],
  ],
  promo: [
    [0, 18, 100, 14, 'a'],
    [20, 23, 44, 4, 't'],
    [68, 22, 14, 6],
  ],
  footer: [
    [0, 26, 100, 24, 'd'],
    [8, 32, 30, 3, 't'],
    [8, 38, 20, 3, 't'],
    [60, 32, 30, 3, 't'],
  ],
};

/** A drawing for the blocks with one look, and for a view this bundle has not met. */
const BLOCKS: Record<string, Box[]> = {
  hours: [
    [6, 8, 30, 4, 't'],
    ...[0, 1, 2, 3].map((i): Box => [6, 16 + i * 7, 40, 3, 't']),
    ...[0, 1, 2, 3].map((i): Box => [56, 16 + i * 7, 30, 3, 'a']),
  ],
  menu: VIEWS['price-table'] ?? [],
  stay: [
    [6, 8, 40, 20],
    [54, 8, 40, 20],
    [6, 32, 30, 4, 't'],
    [54, 32, 30, 4, 't'],
    [36, 40, 28, 6, 'a'],
  ],
  sailings: [
    [6, 8, 88, 8],
    [6, 20, 88, 8],
    [6, 32, 88, 8],
    [80, 10, 12, 4, 'a'],
    [80, 22, 12, 4, 'a'],
    [80, 34, 12, 4, 'a'],
  ],
  timetable: [
    ...[0, 1, 2, 3, 4].map((i): Box => [6 + i * 18, 6, 16, 4, 't']),
    [6, 14, 16, 8, 'a'],
    [42, 14, 16, 8, 'a'],
    [24, 26, 16, 8, 'a'],
    [78, 26, 16, 8, 'a'],
  ],
  shop: cards3,
  gift: [
    [6, 8, 40, 34, 'a'],
    [54, 14, 40, 5, 't'],
    [54, 24, 30, 3, 't'],
    [54, 32, 18, 6],
  ],
  location: [
    [6, 8, 40, 4, 't'],
    [6, 16, 40, 3, 't'],
    [6, 22, 30, 3, 't'],
    [54, 6, 40, 38],
  ],
  footer: VIEWS['footer'] ?? [],
  policies: lines(4, 6, 88),
  pricing: [
    [6, 8, 27, 34],
    [37, 4, 27, 42, 'a'],
    [68, 8, 27, 34],
  ],
  booking: VIEWS['classic'] ?? [],
  inquiry: VIEWS['card'] ?? [],
  newsletter: VIEWS['inline'] ?? [],
  notice: VIEWS['closure'] ?? [],
};

const GENERIC: Box[] = [
  [6, 10, 50, 5, 't'],
  [6, 20, 88, 20],
];

const FILL: Record<Ink, string> = {
  b: 'var(--line)',
  t: 'var(--ink-faint)',
  a: 'var(--accent)',
  d: 'var(--ink-soft)',
};

/** The drawing for one view of one block (`view` empty for a block with one look). */
export function Thumb({ type, view }: { type: string; view: string }): ReactNode {
  const boxes = (view === '' ? undefined : VIEWS[view]) ?? BLOCKS[type] ?? GENERIC;
  return (
    <svg className="bld-thumb" viewBox="0 0 100 50" aria-hidden="true" focusable="false">
      <rect x="0" y="0" width="100" height="50" fill="var(--line-soft)" />
      {boxes.map(([x, y, w, h, ink = 'b'], i) => (
        <rect key={i} x={x} y={y} width={w} height={h} rx={1.5} fill={FILL[ink]} />
      ))}
    </svg>
  );
}

/** Whether a bespoke drawing exists — the gallery test asserts the common ones do. */
export function hasDrawing(type: string, view: string): boolean {
  return (view !== '' && VIEWS[view] !== undefined) || BLOCKS[type] !== undefined;
}
